/**
 * <IconButton> — small reusable circular/subtle-bordered icon button used by
 * the topbar's theme toggle, notifications wrapper, and sparkle/Tweaks button.
 *
 * Mirrors `.icon-btn` from the reference design 2026-04-18 bundle (38×38 grid,
 * 1px subtle border, hover surface-elevated). Logical-property classes only —
 * no `ml-`/`mr-`/`left-`/`right-`/`inset-block-*`/`inset-inline-*`.
 *
 * Quick 260426-qzw: separated from inline JSX to keep the topbar redesign
 * composable and so future surfaces (filters bar, breadcrumb actions) can
 * reuse the chrome without duplicating Tailwind classes.
 */
import * as React from "react";
import { cn } from "../../utils";

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — accessible label for the icon-only button. */
  "aria-label": string;
  /** Optional active visual state (e.g. "tweaks panel open"). */
  active?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, active, type, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(
        "inline-flex items-center justify-center w-9 h-9 rounded-md",
        "border border-subtle bg-surface text-default",
        "hover:bg-surface-elevated transition-colors",
        "focus-visible:focus-ring",
        active === true && "bg-surface-elevated",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
